// "Run locally" wizard — package one or more designed apps + the runtime
// server itself into a single self-contained archive the user can unpack
// and run with one command.
//
// Three-step flow:
//   1. Select applications (multi-select from the user's accessible projects)
//   2. Review + pick archive format (tar.gz / zip)
//   3. Download (triggers the archive stream) + post-download run command

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getDownloadEligibility, type DownloadEligibility } from '@/api/billing'
import { refreshCredits } from '@/store/creditsStore'
import {
  Download, Check, ChevronLeft, ChevronRight, Loader2, Lock, Coins,
  Search, Database, Code, Package, ArrowRight, Copy, Terminal,
  AlertCircle,
} from 'lucide-react'
import {
  getProjects, downloadRuntimePackage, type RuntimePackageFormat,
} from '@/api/workbench'
import type { ProjectData } from '@/types'

type Step = 'pick' | 'review' | 'download'

// The user's intent — which one-liner they want to see after the download.
// Docker and Tarball both produce a `.tar.gz` (same contents); Zip is a
// genuinely different wrapping. The wizard remembers the choice so the
// post-download step can show ONE command instead of a menu of recipes.
type RunRecipeChoice = 'docker' | 'tarball' | 'zip'

function recipeToFormat(r: RunRecipeChoice): RuntimePackageFormat {
  return r === 'zip' ? 'zip' : 'tar.gz'
}

export default function RunLocallyWizard() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('pick')
  const [projects, setProjects] = useState<ProjectData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const [recipe, setRecipe] = useState<RunRecipeChoice>('tarball')
  // Fetched on mount so a plan that doesn't include downloads is told up front,
  // rather than after they've chosen apps and hit a 403.
  const [eligibility, setEligibility] = useState<DownloadEligibility | null>(null)

  // The fee comes out of the selected APPLICATION's wallet (money, not
  // credits), so eligibility is re-fetched with the first selected project to
  // quote that wallet's balance. On mount (nothing selected) it still answers
  // the plan gate.
  const firstSelected = selected.size ? [...selected][0] : undefined
  useEffect(() => {
    getDownloadEligibility(firstSelected)
      .then(setEligibility)
      // Billing being unreachable must not block a download the plan allows —
      // the server gate is authoritative either way.
      .catch(() => setEligibility(null))
  }, [firstSelected])

  useEffect(() => {
    getProjects()
      .then((list) => setProjects(list || []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load projects.'))
      .finally(() => setLoading(false))
  }, [])

  // Drafts can't be packaged (they haven't been saved as a real project).
  const packageable = useMemo(() => projects.filter((p) =>
    p.application.folder_name !== 'Draft Application'
  ), [projects])

  const filtered = useMemo(() => {
    if (!query.trim()) return packageable
    const q = query.toLowerCase()
    return packageable.filter((p) =>
      p.application.title.toLowerCase().includes(q) ||
      p.application.folder_name.toLowerCase().includes(q),
    )
  }, [packageable, query])

  const selectedProjects = useMemo(
    () => packageable.filter((p) => selected.has(p.application.folder_name)),
    [packageable, selected],
  )

  const toggle = (folder: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(folder)) next.delete(folder)
      else next.add(folder)
      return next
    })
  }

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '32px 24px', fontFamily: 'var(--font-primary)' }}>
      {/* Top: title + crumb */}
      <div style={{ marginBottom: 24 }}>
        <Link to="/workbench" style={{
          color: 'var(--color-text-secondary)', fontSize: 14,
          display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none',
        }}>
          <ChevronLeft size={14} /> Back to Workbench
        </Link>
        <h1 style={{ marginTop: 8, marginBottom: 4, fontSize: 25 }}>Run locally</h1>
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 15 }}>
          Bundle one or more of your apps into a single zip you can run on your own
          machine with the standalone Twasta runtime.
        </p>
      </div>

      {eligibility && !eligibility.allowed ? (
        <UpsellPanel eligibility={eligibility} />
      ) : (
      <>
      <Stepper step={step} />

      {step === 'pick' && (
        <PickStep
          loading={loading}
          error={error}
          projects={filtered}
          totalCount={packageable.length}
          query={query}
          setQuery={setQuery}
          selected={selected}
          toggle={toggle}
          onNext={() => setStep('review')}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          selected={selectedProjects}
          recipe={recipe}
          setRecipe={setRecipe}
          eligibility={eligibility}
          onBack={() => setStep('pick')}
          onConfirm={() => setStep('download')}
        />
      )}

      {step === 'download' && (
        <DownloadStep
          projectNames={selectedProjects.map((p) => p.application.folder_name)}
          recipe={recipe}
          onBack={() => setStep('review')}
          onClose={() => navigate('/workbench')}
        />
      )}
      </>
      )}
    </div>
  )
}

/* ── Plan gate ───────────────────────────────────────── */

function UpsellPanel({ eligibility }: { eligibility: DownloadEligibility }) {
  return (
    <div style={{
      padding: 24, border: '1px solid var(--color-border)', borderRadius: 8,
      background: 'var(--color-bg-secondary)', textAlign: 'center',
    }}>
      <Lock size={28} style={{ color: 'var(--color-accent)' }} />
      <h2 style={{ fontSize: 19, margin: '12px 0 6px' }}>
        Available on {eligibility.required_plan_label}
      </h2>
      <p style={{ margin: '0 0 16px', fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
        Downloading a standalone runtime package — your apps plus the Twasta
        runtime server, to run on your own machine or ship to a customer — is part
        of the {eligibility.required_plan_label} plan. You're currently on{' '}
        {eligibility.plan_label}.
      </p>
      <Link to="/payments/plans" className="btn btn-primary" style={{ textDecoration: 'none' }}>
        View plans <ArrowRight size={14} />
      </Link>
    </div>
  )
}

/** The download-fee line on the review step. Hidden entirely when downloads are free. */
function DownloadCostLine({ eligibility }: { eligibility: DownloadEligibility | null }) {
  if (!eligibility || eligibility.fee_minor <= 0) return null
  const short = !eligibility.sufficient
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10,
      padding: '8px 12px', borderRadius: 6, fontSize: 13,
      background: 'var(--color-bg)',
      border: `1px solid ${short ? 'var(--color-danger, #d64545)' : 'var(--color-border)'}`,
    }}>
      <Coins size={14} style={{ flexShrink: 0, marginTop: 1, color: 'var(--color-accent)' }} />
      <span>
        This download costs <strong>{eligibility.fee_display}</strong> ·
        application wallet balance {eligibility.balance_display}
        {short && (
          <span style={{ color: 'var(--color-danger, #d64545)' }}>
            {' — add funds from the application’s Team panel to continue'}
          </span>
        )}
      </span>
    </div>
  )
}

/* ── Stepper ─────────────────────────────────────────── */

function Stepper({ step }: { step: Step }) {
  const items: { id: Step; label: string }[] = [
    { id: 'pick', label: '1. Select apps' },
    { id: 'review', label: '2. Review' },
    { id: 'download', label: '3. Download + run' },
  ]
  const activeIdx = items.findIndex((i) => i.id === step)
  return (
    <div style={{
      display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center',
    }}>
      {items.map((item, i) => (
        <div key={item.id} style={{
          flex: 1, padding: '8px 12px', borderRadius: 8,
          background: i === activeIdx ? 'var(--color-accent-bg, var(--color-bg-secondary))' : 'var(--color-bg-secondary)',
          border: i === activeIdx ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
          color: i === activeIdx ? 'var(--color-accent)' : i < activeIdx ? 'var(--color-text)' : 'var(--color-text-secondary)',
          fontSize: 14, fontWeight: 500, textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          {i < activeIdx && <Check size={14} />}
          {item.label}
        </div>
      ))}
    </div>
  )
}

/* ── Step 1: pick projects ───────────────────────────── */

interface PickStepProps {
  loading: boolean
  error: string | null
  projects: ProjectData[]
  totalCount: number
  query: string
  setQuery: (q: string) => void
  selected: Set<string>
  toggle: (folder: string) => void
  onNext: () => void
}

function PickStep({
  loading, error, projects, totalCount, query, setQuery,
  selected, toggle, onNext,
}: PickStepProps) {
  return (
    <div>
      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0.5rem 0.75rem', marginBottom: 16,
        border: '1px solid var(--color-border)', borderRadius: 8,
        background: 'var(--color-bg)',
      }}>
        <Search size={16} style={{ color: 'var(--color-text-secondary)' }} />
        <input
          type="text"
          placeholder="Search by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            flex: 1, background: 'transparent', border: 'none',
            outline: 'none', color: 'var(--color-text)', fontSize: 15,
          }}
        />
      </div>

      {loading && <Status text="Loading your projects…" />}
      {error && <Status text={error} tone="error" />}
      {!loading && !error && totalCount === 0 && (
        <Status text="You don't have any saved projects yet. Build one in the Builder, then come back." />
      )}
      {!loading && !error && totalCount > 0 && projects.length === 0 && (
        <Status text="No projects match your search." />
      )}

      {/* List */}
      {!loading && projects.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {projects.map((p) => {
            const app = p.application
            const checked = selected.has(app.folder_name)
            return (
              <button
                key={app.folder_name}
                type="button"
                onClick={() => toggle(app.folder_name)}
                style={{
                  display: 'flex', gap: 12, alignItems: 'center',
                  padding: '0.75rem 1rem', border: `1px solid ${checked ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  borderRadius: 8, background: checked ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
                  boxShadow: checked ? '0 0 0 1px var(--color-accent) inset' : 'none',
                  cursor: 'pointer', textAlign: 'left',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <div style={{
                  width: 22, height: 22, borderRadius: 4,
                  border: `1.5px solid ${checked ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  background: checked ? 'var(--color-accent)' : 'transparent',
                  color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {checked && <Check size={14} />}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 15 }}>{app.title || app.folder_name}</strong>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                      {app.folder_name}
                    </span>
                  </div>
                  <div style={{
                    marginTop: 4, fontSize: 13, color: 'var(--color-text-secondary)',
                    display: 'flex', gap: 12, flexWrap: 'wrap',
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Database size={12} />
                      {(app as { db_mode?: string }).db_mode || 'postgres'}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Code size={12} />
                      {(app as { biz_logic?: string }).biz_logic || 'python'}
                    </span>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {/* Footer */}
      <Footer
        left={
          <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            {selected.size} selected
          </span>
        }
        right={
          <button
            type="button"
            className="btn btn-primary"
            onClick={onNext}
            disabled={selected.size === 0}
          >
            Next <ChevronRight size={14} />
          </button>
        }
      />
    </div>
  )
}

/* ── Step 2: review ──────────────────────────────────── */

function ReviewStep({
  selected, recipe, setRecipe, eligibility, onBack, onConfirm,
}: {
  selected: ProjectData[]
  recipe: RunRecipeChoice
  setRecipe: (r: RunRecipeChoice) => void
  eligibility: DownloadEligibility | null
  onBack: () => void
  onConfirm: () => void
}) {
  const needsPostgres = selected.some((p) =>
    ((p.application as { db_mode?: string }).db_mode || 'postgres') === 'postgres'
  )
  return (
    <div>
      <div style={{
        padding: 16, border: '1px solid var(--color-border)',
        borderRadius: 8, background: 'var(--color-bg-secondary)',
        marginBottom: 16,
      }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text)', lineHeight: 1.5 }}>
          The download is <strong>self-contained</strong>: the Twasta runtime
          server, the React client (pre-built), and{' '}
          <strong>{selected.length}</strong> app{selected.length === 1 ? '' : 's'}{' '}
          you selected. Unpack it, run one command, and you're up.
        </p>
        {needsPostgres && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 6,
            background: 'var(--color-warning-bg, #fef3c7)',
            border: '1px solid var(--color-warning, #f59e0b)',
            color: 'var(--color-text)',
            fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              One or more apps use Postgres. The bundled <code>start.sh</code>{' '}
              will prompt you for connection details on first run and remember
              them for next time. With Docker, the <code>postgres</code> profile
              spins up a managed Postgres alongside the runtime.
            </span>
          </div>
        )}
        <DownloadCostLine eligibility={eligibility} />
      </div>

      {/* App list */}
      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        {selected.map((p) => {
          const app = p.application
          const dbMode = (app as { db_mode?: string }).db_mode || 'postgres'
          const bizLogic = (app as { biz_logic?: string }).biz_logic || 'python'
          return (
            <div key={app.folder_name} style={{
              padding: '0.75rem 1rem', border: '1px solid var(--color-border)',
              borderRadius: 8, background: 'var(--color-bg)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <Package size={20} style={{ color: 'var(--color-accent)' }} />
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: 15 }}>{app.title || app.folder_name}</strong>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                  {dbMode} · {bizLogic}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* How will you run it? */}
      <h3 style={{ fontSize: 15, margin: '0 0 4px' }}>How will you run it?</h3>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
        The download is self-contained either way — pick the one matching what
        you have installed. Same files inside, different wrapper.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        <RecipeCard
          value="docker"
          selected={recipe === 'docker'}
          onClick={() => setRecipe('docker')}
          title="Docker"
          requires="Docker + docker compose"
          blurb=".tar.gz. No Python install on your side; the image is built from the bundle on first run."
        />
        <RecipeCard
          value="tarball"
          selected={recipe === 'tarball'}
          onClick={() => setRecipe('tarball')}
          title="Tarball"
          requires="Python 3.12"
          blurb=".tar.gz. Two commands: setup.sh once, then start.sh to launch."
        />
        <RecipeCard
          value="zip"
          selected={recipe === 'zip'}
          onClick={() => setRecipe('zip')}
          title="Zip"
          requires="Python 3.12 (Windows)"
          blurb=".zip with setup.bat + start.bat. Same scripts inside as the tarball."
        />
      </div>

      <Footer
        left={
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            <ChevronLeft size={14} /> Back
          </button>
        }
        right={
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={!!eligibility && eligibility.fee_minor > 0 && !eligibility.sufficient}
          >
            {eligibility && eligibility.fee_minor > 0
              ? `Confirm — pay ${eligibility.fee_display} from the app wallet`
              : 'Build the package'} <ArrowRight size={14} />
          </button>
        }
      />
    </div>
  )
}

function RecipeCard({
  value, selected, onClick, title, requires, blurb,
}: {
  value: RunRecipeChoice
  selected: boolean
  onClick: () => void
  title: string
  requires: string
  blurb: string
}) {
  void value
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '0.75rem 1rem',
        background: selected ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
        border: `1px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 8,
        boxShadow: selected ? '0 0 0 1px var(--color-accent) inset' : 'none',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <strong style={{ fontSize: 15 }}>{title}</strong>
        {selected && <span style={{ fontSize: 13, color: 'var(--color-accent)' }}>selected</span>}
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
        requires {requires}
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 6, lineHeight: 1.4 }}>
        {blurb}
      </div>
    </button>
  )
}

/* ── Step 3: download + run instructions ─────────────── */

function DownloadStep({
  projectNames, recipe, onBack, onClose,
}: {
  projectNames: string[]
  recipe: RunRecipeChoice
  onBack: () => void
  onClose: () => void
}) {
  const [downloading, setDownloading] = useState(false)
  const [done, setDone] = useState(false)
  const [filename, setFilename] = useState<string | null>(null)
  const [needsPostgres, setNeedsPostgres] = useState(false)

  // Auto-trigger the download once on mount so the user doesn't have to
  // click an extra button. They can re-trigger via the button if needed.
  useEffect(() => {
    void doDownload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function doDownload() {
    setDownloading(true)
    try {
      const fmt = recipeToFormat(recipe)
      const summary = await downloadRuntimePackage(projectNames, fmt)
      setFilename(summary.filename)
      setNeedsPostgres(summary.needsPostgres)
      setDone(true)
      // The server debited on the way out — pull the new balance so the user
      // menu isn't showing a number that is already stale.
      refreshCredits()
      toast.success(
        `Downloaded ${summary.filename} (${summary.projectCount} app${summary.projectCount === 1 ? '' : 's'}, ${summary.fileCount} files).`,
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Download failed.'
      toast.error(msg)
    } finally {
      setDownloading(false)
    }
  }

  const archiveName = filename
    || `twasta-runtime-package.${recipeToFormat(recipe)}`
  const folderName = (filename || 'twasta-runtime-package').replace(/\.(tar\.gz|zip)$/, '')

  // ONE recipe — the user already chose how they want to run it in the
  // review step. Showing more would be paternalistic.
  const steps: string[] = (() => {
    if (recipe === 'docker') {
      return [
        `tar xzf ${archiveName}`,
        `cd ${folderName}/runtime/docker`,
        needsPostgres ? 'docker compose --profile postgres up' : 'docker compose --profile sqlite up',
        'Open http://localhost:8088/',
      ]
    }
    if (recipe === 'zip') {
      // Windows users: extract, then setup.bat once, start.bat to launch.
      // The .sh variants are also in the archive for WSL / Git Bash users.
      return [
        `unzip ${archiveName}`,
        `cd ${folderName}`,
        'scripts\\setup.bat',
        'scripts\\start.bat',
        'Open http://localhost:8088/',
      ]
    }
    return [
      `tar xzf ${archiveName}`,
      `cd ${folderName}`,
      './scripts/setup.sh',
      './scripts/start.sh',
      'Open http://localhost:8088/',
    ]
  })()
  const recipeLabel =
    recipe === 'docker' ? 'Docker'
    : recipe === 'zip'  ? 'Python 3.12 on Windows'
    :                     'Python 3.12 (macOS / Linux / WSL)'

  return (
    <div>
      {/* Download status panel */}
      <div style={{
        padding: 20, border: '1px solid var(--color-border)',
        borderRadius: 8, background: 'var(--color-bg)',
        marginBottom: 16, textAlign: 'center',
      }}>
        {downloading && !done && (
          <>
            <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
            <p style={{ margin: '12px 0 0', fontSize: 15, color: 'var(--color-text-secondary)' }}>
              Building your self-contained package…
            </p>
          </>
        )}
        {done && (
          <>
            <Check size={28} style={{ color: 'var(--color-success, #16a34a)' }} />
            <p style={{ margin: '12px 0 0', fontSize: 15 }}>
              Downloaded <strong>{filename}</strong>.
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Contains: the Twasta runtime server + pre-built React client +
              your {projectNames.length} app{projectNames.length === 1 ? '' : 's'}.
            </p>
          </>
        )}
        {!downloading && !done && (
          <button type="button" className="btn btn-primary" onClick={doDownload}>
            <Download size={14} /> Retry download
          </button>
        )}
      </div>

      {/* ONE recipe — the user's choice from the previous step */}
      <div style={{
        padding: 16, borderRadius: 8, border: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
      }}>
        <h2 style={{
          fontSize: 15, margin: '0 0 8px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Terminal size={14} /> Run it with {recipeLabel}
        </h2>
        {recipe !== 'docker' && (
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
            <strong>setup</strong> runs once — creates a Python venv, installs
            the engine + server. <strong>start</strong> launches the server
            (run it every time you want to boot).
            {needsPostgres && (
              <> On first start you'll be prompted for Postgres connection
              details; they're saved to <code>.env</code> so subsequent starts
              are non-interactive.</>
            )}
          </p>
        )}
        {recipe === 'docker' && needsPostgres && (
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
            The <code>postgres</code> profile spins up a managed Postgres
            container alongside the runtime — no separate DB install.
          </p>
        )}
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.9 }}>
          {steps.map((s, i) => (
            <li key={i}>
              {s.startsWith('Open ')
                ? s
                : <CopyableCode text={s} />}
            </li>
          ))}
        </ol>
        <p style={{
          margin: '12px 0 0', fontSize: 13, color: 'var(--color-text-secondary)',
        }}>
          Prefer a different runner? The bundled <code>README.md</code>{' '}
          has all three recipes inside the archive — they all operate on the
          same files; no extra download.
        </p>
      </div>

      <Footer
        left={
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            <ChevronLeft size={14} /> Back
          </button>
        }
        right={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      />
    </div>
  )
}

function CopyableCode({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <code
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          /* clipboard may be blocked — render is still readable */
        }
      }}
      title="Click to copy"
      style={{
        background: 'var(--color-bg)', padding: '2px 6px',
        borderRadius: 4, fontSize: 13, fontFamily: 'monospace',
        border: '1px solid var(--color-border)', cursor: 'copy',
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}
    >
      {text}
      <Copy size={11} style={{ opacity: 0.5 }} />
      {copied && <span style={{ color: 'var(--color-accent)', marginLeft: 4 }}>copied</span>}
    </code>
  )
}

/* ── Shared helpers ──────────────────────────────────── */

function Status({ text, tone }: { text: string; tone?: 'error' }) {
  return (
    <div style={{
      padding: 16, textAlign: 'center',
      color: tone === 'error' ? 'var(--color-error, #b91c1c)' : 'var(--color-text-secondary)',
      fontSize: 14,
    }}>
      {text}
    </div>
  )
}

function Footer({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  return (
    <div style={{
      marginTop: 24, paddingTop: 16,
      borderTop: '1px solid var(--color-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <div>{left}</div>
      <div>{right}</div>
    </div>
  )
}
